import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';

const useStyles = makeStyles(() => ({
  paper: {
    width: '100%',
    maxWidth: 500,
    borderRadius: 20,
    border: '1px solid #e1e7e4',
    padding: 28,
    boxShadow: 'none',
  },
  headRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  },
  iconChip: {
    flex: 'none',
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#fbeceb',
    border: '1px solid #f2cfcc',
    color: '#d14343',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    margin: 0,
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 21,
    lineHeight: 1.25,
    fontWeight: 700,
    letterSpacing: '-.01em',
    color: '#16141d',
  },
  body: {
    margin: '8px 0 0',
    fontSize: 13.8,
    lineHeight: 1.55,
    color: '#5f5d75',
  },
  bodyStrong: {
    color: '#16141d',
    fontWeight: 600,
  },
  closeButton: {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 8,
    border: 'none',
    background: 'none',
    color: '#5b6661',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#eef2f0',
      color: '#16141d',
    },
  },
  warningBox: {
    marginTop: 18,
    padding: '14px 16px',
    border: '1px solid #f2cfcc',
    borderRadius: 12,
    backgroundColor: '#fdf6f5',
  },
  warningTitle: {
    fontSize: 13.4,
    fontWeight: 700,
    color: '#a8332f',
  },
  warningBody: {
    marginTop: 4,
    fontSize: 13,
    lineHeight: 1.55,
    color: '#5f5d75',
  },
  actionRow: {
    marginTop: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 44,
    padding: '0 20px',
    border: '1.5px solid #d7dbe0',
    borderRadius: 10,
    backgroundColor: '#fff',
    color: '#16141d',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': {
      borderColor: '#b6bcc4',
      backgroundColor: '#f6f8f7',
    },
  },
  deleteButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 44,
    padding: '0 22px',
    border: 'none',
    borderRadius: 10,
    backgroundColor: '#d14343',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: '#b8332f',
    },
    '&:disabled': {
      backgroundColor: '#e7ebe9',
      color: '#9aa5a0',
      cursor: 'not-allowed',
    },
  },
}));

const TrashIcon = () => (
  <svg width={21} height={21} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 11v6M14 11v6M4 7h16M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const CloseIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export const DeletePaymentMethodDialog = (
  { open, methodLabel, accountName, loading, onCancel, onConfirm }: {
    open: boolean;
    methodLabel: string;
    accountName?: string;
    loading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
  },
) => {
  const classes = useStyles();

  return (
    <Dialog
      id="delete-payment-method-dialog"
      open={open}
      onClose={onCancel}
      classes={{ paper: classes.paper }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(22,20,29,.42)' } }}
    >
      <Box className={classes.headRow}>
        <Box component="span" className={classes.iconChip}>
          <TrashIcon />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography component="h2" className={classes.title}>
            <FormattedMessage id="page.payment.delete_dialog.title" />
          </Typography>
          <Typography className={classes.body}>
            <FormattedMessage
              id="page.payment.delete_dialog.body"
              values={{
                method: <span className={classes.bodyStrong}>{methodLabel}</span>,
                accountName,
              }}
            />
          </Typography>
        </Box>
        <button type="button" aria-label="Close" className={classes.closeButton} onClick={onCancel}>
          <CloseIcon />
        </button>
      </Box>
      <Box className={classes.warningBox}>
        <Typography className={classes.warningTitle}>
          <FormattedMessage id="page.payment.delete_dialog.warning.title" />
        </Typography>
        <Typography className={classes.warningBody}>
          <FormattedMessage id="page.payment.delete_dialog.warning.body" />
        </Typography>
      </Box>
      <Box className={classes.actionRow}>
        <button type="button" className={classes.cancelButton} onClick={onCancel} disabled={loading}>
          <FormattedMessage id="page.payment.delete_dialog.cancel" />
        </button>
        <button
          type="button"
          id="delete-payment-method-confirm-btn"
          className={classes.deleteButton}
          onClick={onConfirm}
          disabled={loading}
        >
          <FormattedMessage id="page.payment.delete_dialog.delete" />
        </button>
      </Box>
    </Dialog>
  );
};
